import React, { useState, useRef } from "react";
import { View, StyleSheet, Pressable, GestureResponderEvent, ViewStyle, TextStyle } from "react-native";
import { Menu } from "react-native-paper";
import { CaretLeftIcon, CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale } from "../../../styles/design-system";

type NavigationStyle = "text" | "icons";

interface AdminTablePaginationProps {
  currentPage: number;
  totalPages?: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  /** Navigation button style: "text" shows Previous/Next, "icons" shows caret icons */
  navigationStyle?: NavigationStyle;
  /** Whether to show ellipsis for many pages */
  showEllipsis?: boolean;
  /** Whether to show items per page selector */
  showItemsPerPageSelector?: boolean;
  /** Custom container style override */
  containerStyle?: ViewStyle;
  /** Custom active page button style override */
  activePageButtonStyle?: ViewStyle;
  /** Custom active page text style override */
  activePageTextStyle?: TextStyle;
}

const AdminTablePagination: React.FC<AdminTablePaginationProps> = ({
  currentPage,
  totalPages: totalPagesProp,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [5, 10, 20, 50],
  navigationStyle = "text",
  showEllipsis = true,
  showItemsPerPageSelector = true,
  containerStyle,
  activePageButtonStyle,
  activePageTextStyle,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef<View>(null);

  // Calculate total pages (use prop if provided, otherwise calculate)
  const totalPages = totalPagesProp ?? Math.ceil(totalItems / itemsPerPage);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Don't render if there are no items
  if (totalItems === 0) {
    return null;
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (showEllipsis && startPage > 1) {
      pages.push(
        <Pressable key={1} onPress={() => handlePageClick(1)} style={styles.pageButton}>
          <CustomText text="1" size="s" useTranslationText={false} style={styles.pageText} />
        </Pressable>
      );
      if (startPage > 2) {
        pages.push(
          <View key="dots1" style={styles.dots}>
            <CustomText text="..." size="s" useTranslationText={false} style={styles.pageText} />
          </View>
        );
      }
    }

    for (
      let i = showEllipsis ? startPage : 1;
      i <= (showEllipsis ? endPage : Math.min(maxVisiblePages, totalPages));
      i++
    ) {
      // For non-ellipsis mode, adjust page number based on current position
      const pageNum = showEllipsis ? i : getPageNumberForPosition(i, currentPage, totalPages, maxVisiblePages);
      pages.push(
        <Pressable
          key={pageNum}
          onPress={() => handlePageClick(pageNum)}
          style={[
            styles.pageButton,
            pageNum === currentPage && styles.activePageButton,
            pageNum === currentPage && activePageButtonStyle,
          ]}>
          <CustomText
            text={pageNum.toString()}
            size="s"
            useTranslationText={false}
            style={[
              styles.pageText,
              pageNum === currentPage && styles.activePageText,
              pageNum === currentPage && activePageTextStyle,
            ]}
          />
        </Pressable>
      );
    }

    if (showEllipsis && endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <View key="dots2" style={styles.dots}>
            <CustomText text="..." size="s" useTranslationText={false} style={styles.pageText} />
          </View>
        );
      }
      pages.push(
        <Pressable key={totalPages} onPress={() => handlePageClick(totalPages)} style={styles.pageButton}>
          <CustomText text={totalPages.toString()} size="s" useTranslationText={false} style={styles.pageText} />
        </Pressable>
      );
    }

    return pages;
  };

  // Helper for non-ellipsis mode: calculate which page number to show at each position
  const getPageNumberForPosition = (position: number, current: number, total: number, maxButtons: number): number => {
    if (total <= maxButtons) {
      return position;
    } else if (current <= Math.ceil(maxButtons / 2)) {
      return position;
    } else if (current >= total - Math.floor(maxButtons / 2)) {
      return total - maxButtons + position;
    } else {
      return current - Math.floor(maxButtons / 2) + position - 1;
    }
  };

  const handleMenuOpen = (event: GestureResponderEvent) => {
    if (dropdownRef.current) {
      dropdownRef.current.measure((fx, fy, width, height, px, py) => {
        setMenuPosition({ x: px, y: py + height });
        setMenuVisible(true);
      });
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.leftSection}>
        <CustomText
          text={`Showing ${startItem}-${endItem} of ${totalItems.toLocaleString()}`}
          size="s"
          style={styles.infoText}
          useTranslationText={false}
        />
      </View>

      <View style={styles.centerSection}>
        {navigationStyle === "text" ? (
          <Pressable
            onPress={handlePreviousPage}
            disabled={currentPage === 1}
            style={[styles.navButton, currentPage === 1 && styles.disabledButton]}>
            <CustomText text="Previous" size="s" style={styles.navText} useTranslationText={false} />
          </Pressable>
        ) : (
          <Pressable
            onPress={handlePreviousPage}
            disabled={currentPage === 1}
            style={[styles.iconNavButton, currentPage === 1 && styles.disabledButton]}
            accessibilityLabel="Previous page">
            <CaretLeftIcon size={16} color={colors.grey[600]} />
          </Pressable>
        )}

        <View style={styles.pageNumbers}>{renderPageNumbers()}</View>

        {navigationStyle === "text" ? (
          <Pressable
            onPress={handleNextPage}
            disabled={currentPage === totalPages}
            style={[styles.navButton, currentPage === totalPages && styles.disabledButton]}>
            <CustomText text="Next" size="s" style={styles.navText} useTranslationText={false} />
          </Pressable>
        ) : (
          <Pressable
            onPress={handleNextPage}
            disabled={currentPage === totalPages}
            style={[styles.iconNavButton, currentPage === totalPages && styles.disabledButton]}
            accessibilityLabel="Next page">
            <CaretRightIcon size={16} color={colors.grey[600]} />
          </Pressable>
        )}
      </View>

      {showItemsPerPageSelector && onItemsPerPageChange && (
        <View style={styles.rightSection}>
          <CustomText text="Items per page:" size="s" style={styles.itemsPerPageLabel} useTranslationText={false} />
          <Pressable ref={dropdownRef} onPress={handleMenuOpen} style={styles.itemsPerPageButton}>
            <CustomText
              text={itemsPerPage.toString()}
              size="s"
              useTranslationText={false}
              style={styles.itemsPerPageText}
            />
            <CaretDownIcon size={16} />
          </Pressable>

          <Menu
            visible={menuVisible}
            onDismiss={() => setMenuVisible(false)}
            anchor={{ x: menuPosition.x, y: menuPosition.y }}
            contentStyle={styles.menu}>
            {itemsPerPageOptions.map((option) => (
              <Menu.Item
                key={option}
                onPress={() => {
                  onItemsPerPageChange(option);
                  setMenuVisible(false);
                }}
                title={option.toString()}
                titleStyle={styles.menuItemText}
              />
            ))}
          </Menu>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  leftSection: {
    flex: 1,
  },
  centerSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rightSection: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  infoText: {
    color: colors.grey[500],
  },
  navButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: radii.sm,
  },
  iconNavButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: radii.md,
    minWidth: 32,
    alignItems: "center",
  },
  disabledButton: {
    opacity: 0.5,
  },
  navText: {
    color: colors.grey[700],
  },
  pageNumbers: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  pageButton: {
    minWidth: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.sm,
  },
  activePageButton: {
    backgroundColor: colors.brand[600],
  },
  pageText: {
    color: colors.grey[700],
  },
  activePageText: {
    color: colors.white,
    fontWeight: "600",
  },
  dots: {
    paddingHorizontal: 4,
  },
  itemsPerPageLabel: {
    color: colors.grey[500],
  },
  itemsPerPageButton: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.sm,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 4,
  },
  itemsPerPageText: {
    color: colors.grey[700],
  },
  menu: {
    backgroundColor: colors.white,
    marginTop: 4,
  },
  menuItemText: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[700],
  },
});

export default AdminTablePagination;
